import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { DollarSign, Package, FileText, AlertTriangle, TrendingUp, X, ShoppingCart } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const fmtCurrency = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showLowStock, setShowLowStock] = useState(false);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [showOpenPOs, setShowOpenPOs] = useState(false);
  const [openPOs, setOpenPOs] = useState([]);
  const [showPending, setShowPending] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [showRecent30, setShowRecent30] = useState(false);
  const [recent30Orders, setRecent30Orders] = useState([]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [statsData, analyticsData] = await Promise.all([
        api.getDashboardStats(), api.getAnalytics()
      ]);
      setStats(statsData);
      setAnalytics(analyticsData);
    } catch (error) { console.error('Failed to load data:', error); }
    finally { setLoading(false); }
  };

  const toggleLowStock = async () => {
    if (!showLowStock) {
      try { const data = await api.request('/api/dashboard/low-stock-items'); setLowStockItems(data); } catch {}
    }
    setShowLowStock(!showLowStock);
    setShowOpenPOs(false); setShowPending(false); setShowRecent30(false);
  };

  const toggleOpenPOs = async () => {
    if (!showOpenPOs) {
      try { const data = await api.request('/api/dashboard/open-pos'); setOpenPOs(data); } catch {}
    }
    setShowOpenPOs(!showOpenPOs);
    setShowLowStock(false); setShowPending(false); setShowRecent30(false);
  };

  const togglePending = async () => {
    if (!showPending) {
      try { const data = await api.request('/api/dashboard/pending-orders'); setPendingOrders(data); } catch {}
    }
    setShowPending(!showPending);
    setShowLowStock(false); setShowOpenPOs(false); setShowRecent30(false);
  };

  const toggleRecent30 = async () => {
    if (!showRecent30) {
      try { const data = await api.request('/api/dashboard/orders-last-30d'); setRecent30Orders(data); } catch {}
    }
    setShowRecent30(!showRecent30);
    setShowLowStock(false); setShowOpenPOs(false); setShowPending(false);
  };

  if (loading) return <div className="p-6">Loading...</div>;

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold tracking-tight">Dashboard</h2>
        <p className="text-muted-foreground">Overview of your inventory operations</p>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <div className={`bg-card p-6 rounded-lg border cursor-pointer transition-colors ${showRecent30 ? 'border-teal-400 ring-1 ring-teal-400' : 'border-border hover:border-teal-300'}`}
          onClick={toggleRecent30} data-testid="orders-30d-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Sales Orders (30d)</p>
              <p className="text-2xl font-semibold tabular-nums mt-2">{stats?.all_orders_30d || 0}</p>
              <p className="text-xs text-teal-600 mt-1">Click to view orders</p>
            </div>
            <div className="p-3 bg-teal-500/10 rounded-lg"><ShoppingCart className="h-6 w-6 text-teal-600" /></div>
          </div>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Sales (30d)</p>
              <p className="text-2xl font-semibold tabular-nums mt-2">{fmtCurrency(stats?.total_sales_amount)}</p>
              <p className="text-xs text-muted-foreground mt-1">{stats?.total_sales_count || 0} orders</p>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg"><DollarSign className="h-6 w-6 text-primary" /></div>
          </div>
        </div>

        <div className={`bg-card p-6 rounded-lg border cursor-pointer transition-colors ${showLowStock ? 'border-yellow-400 ring-1 ring-yellow-400' : 'border-border hover:border-yellow-300'}`}
          onClick={toggleLowStock} data-testid="low-stock-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Low Stock Items</p>
              <p className="text-2xl font-semibold tabular-nums mt-2">{stats?.low_stock_count || 0}</p>
              <p className="text-xs text-yellow-600 mt-1">Click to view items</p>
            </div>
            <div className="p-3 bg-yellow-500/10 rounded-lg"><AlertTriangle className="h-6 w-6 text-yellow-600" /></div>
          </div>
        </div>

        <div className={`bg-card p-6 rounded-lg border cursor-pointer transition-colors ${showOpenPOs ? 'border-blue-400 ring-1 ring-blue-400' : 'border-border hover:border-blue-300'}`}
          onClick={toggleOpenPOs} data-testid="open-pos-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Open POs</p>
              <p className="text-2xl font-semibold tabular-nums mt-2">{stats?.open_po_count || 0}</p>
              <p className="text-xs text-blue-600 mt-1">Click to view POs</p>
            </div>
            <div className="p-3 bg-blue-500/10 rounded-lg"><FileText className="h-6 w-6 text-blue-600" /></div>
          </div>
        </div>

        <div className={`bg-card p-6 rounded-lg border cursor-pointer transition-colors ${showPending ? 'border-green-400 ring-1 ring-green-400' : 'border-border hover:border-green-300'}`}
          onClick={togglePending} data-testid="pending-orders-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Pending Sales Orders</p>
              <p className="text-2xl font-semibold tabular-nums mt-2">{stats?.pending_orders_count || 0}</p>
              <p className="text-xs text-green-600 mt-1">Click to view orders</p>
            </div>
            <div className="p-3 bg-green-500/10 rounded-lg"><Package className="h-6 w-6 text-green-600" /></div>
          </div>
        </div>
      </div>

      {/* Low Stock Items Popup */}
      {showLowStock && (
        <div className="bg-card rounded-lg border border-yellow-300 overflow-hidden" data-testid="low-stock-list">
          <div className="flex items-center justify-between px-4 py-3 bg-yellow-50 border-b border-yellow-200">
            <h3 className="text-sm font-semibold text-yellow-800">Low Stock Items ({lowStockItems.length})</h3>
            <button onClick={() => setShowLowStock(false)} className="p-1 hover:bg-yellow-100 rounded"><X className="h-4 w-4" /></button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-muted/30">
              <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Product</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">SKU</th>
              <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">On Hand</th>
              <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Reorder Pt</th>
            </tr></thead>
            <tbody className="divide-y">
              {lowStockItems.length === 0 ? (
                <tr><td colSpan="4" className="px-4 py-6 text-center text-muted-foreground">No low stock items</td></tr>
              ) : lowStockItems.map((item, i) => (
                <tr key={i} className="hover:bg-muted/20">
                  <td className="px-4 py-2">
                    <Link to={`/products/${item.product_id}`} className="text-primary hover:underline font-medium" data-testid={`low-stock-item-${i}`}>
                      {item.product_name} {item.variant_name && `- ${item.variant_name}`}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{item.sku}</td>
                  <td className={`px-4 py-2 text-right font-medium ${item.quantity_on_hand === 0 ? 'text-red-600' : 'text-yellow-600'}`}>{item.quantity_on_hand}</td>
                  <td className="px-4 py-2 text-right">{item.reorder_point}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Open POs Popup */}
      {showOpenPOs && (
        <div className="bg-card rounded-lg border border-blue-300 overflow-hidden" data-testid="open-pos-list">
          <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border-b border-blue-200">
            <h3 className="text-sm font-semibold text-blue-800">Open Purchase Orders ({openPOs.length})</h3>
            <button onClick={() => setShowOpenPOs(false)} className="p-1 hover:bg-blue-100 rounded"><X className="h-4 w-4" /></button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-muted/30">
              <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">PO #</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Vendor</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Date</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Status</th>
              <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Total</th>
            </tr></thead>
            <tbody className="divide-y">
              {openPOs.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-6 text-center text-muted-foreground">No open purchase orders</td></tr>
              ) : openPOs.map(po => (
                <tr key={po.id} className="hover:bg-muted/20">
                  <td className="px-4 py-2">
                    <Link to={`/purchase-orders/${po.id}`} className="text-primary hover:underline font-medium">{po.po_number}</Link>
                  </td>
                  <td className="px-4 py-2">{po.vendor_name}</td>
                  <td className="px-4 py-2 text-muted-foreground">{fmtDate(po.order_date)}</td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 text-xs rounded-full font-medium ${po.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{po.status}</span></td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtCurrency(po.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending Orders Popup */}
      {showPending && (
        <div className="bg-card rounded-lg border border-green-300 overflow-hidden" data-testid="pending-orders-list">
          <div className="flex items-center justify-between px-4 py-3 bg-green-50 border-b border-green-200">
            <h3 className="text-sm font-semibold text-green-800">Pending Orders ({pendingOrders.length})</h3>
            <button onClick={() => setShowPending(false)} className="p-1 hover:bg-green-100 rounded"><X className="h-4 w-4" /></button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-muted/30">
              <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Order #</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Customer</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Date</th>
              <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Status</th>
              <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Total</th>
            </tr></thead>
            <tbody className="divide-y">
              {pendingOrders.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-6 text-center text-muted-foreground">No pending orders</td></tr>
              ) : pendingOrders.map(o => (
                <tr key={o.id} className="hover:bg-muted/20">
                  <td className="px-4 py-2">
                    <Link to={`/sales-orders/${o.id}`} className="text-primary hover:underline font-medium">{o.order_number}</Link>
                  </td>
                  <td className="px-4 py-2">{o.customer_name || 'Walk-in'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 text-xs rounded-full font-medium ${o.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{o.status}</span></td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtCurrency(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drill-down: All Sales Orders from the last 30 days. Each row links to the SO detail. */}
      {showRecent30 && (
        <div className="bg-card rounded-lg border border-teal-300 overflow-hidden" data-testid="orders-30d-list">
          <div className="flex items-center justify-between px-4 py-3 bg-teal-50 border-b border-teal-200">
            <h3 className="text-sm font-semibold text-teal-800">Sales Orders — Last 30 Days ({recent30Orders.length})</h3>
            <button onClick={() => setShowRecent30(false)} className="p-1 hover:bg-teal-100 rounded" data-testid="close-orders-30d"><X className="h-4 w-4" /></button>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/30 z-10"><tr className="border-b">
                <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Order #</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Customer</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Lease</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Date</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Payment</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Total</th>
              </tr></thead>
              <tbody className="divide-y">
                {recent30Orders.length === 0 ? (
                  <tr><td colSpan="7" className="px-4 py-6 text-center text-muted-foreground">No orders in the last 30 days.</td></tr>
                ) : recent30Orders.map(o => (
                  <tr key={o.id} className="hover:bg-muted/20 cursor-pointer" onClick={() => navigate(`/sales-orders/${o.id}`)} data-testid={`orders-30d-row-${o.order_number}`}>
                    <td className="px-4 py-2">
                      <Link to={`/sales-orders/${o.id}`} className="text-primary hover:underline font-medium" onClick={(e) => e.stopPropagation()}>{o.order_number}</Link>
                    </td>
                    <td className="px-4 py-2">{o.customer_name || 'Walk-in'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{o.lease_name || '—'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{fmtDate(o.order_date || o.created_at)}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium capitalize ${o.status === 'completed' ? 'bg-green-100 text-green-700' : o.status === 'confirmed' ? 'bg-blue-100 text-blue-700' : o.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>{o.status}</span>
                      {o.return_status === 'partially_returned' && <span className="ml-1 text-xs text-red-600">Partial</span>}
                      {o.return_status === 'fully_returned' && <span className="ml-1 text-xs text-red-600">Returned</span>}
                    </td>
                    <td className="px-4 py-2 capitalize text-xs text-muted-foreground">{o.payment_status || '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtCurrency(o.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Charts */}
      {analytics && (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Sales Trend */}
          <div className="bg-card p-6 rounded-lg border border-border">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Sales Trend (30 Days)</h3>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={analytics.sales_trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line type="monotone" dataKey="total" stroke="#0ea5e9" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Top Products - 10 items, last 30 days */}
          <div className="bg-card p-6 rounded-lg border border-border">
            <h3 className="text-lg font-semibold mb-4">Top Selling Items by Quantity (Last 30 Days)</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={analytics.top_products?.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="variant_name" tick={{ fontSize: 9 }} angle={-25} textAnchor="end" height={90} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => `${v} units`} />
                <Bar dataKey="total_quantity" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Category Breakdown - specific categories */}
          {analytics.category_breakdown && analytics.category_breakdown.length > 0 && (
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-semibold mb-4">Products by Category</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={analytics.category_breakdown} dataKey="count" nameKey="_id" cx="50%" cy="50%" outerRadius={80} label>
                    {analytics.category_breakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Recent Sales */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-6"><h3 className="text-lg font-semibold">Recent Sales</h3></div>
        <div className="border-t border-border">
          {stats?.recent_sales && stats.recent_sales.length > 0 ? (
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Order #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stats.recent_sales.map((order) => (
                  <tr key={order.id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 text-sm">
                      <Link to={`/sales-orders/${order.id}`} className="text-primary hover:underline font-medium">{order.order_number}</Link>
                    </td>
                    <td className="px-6 py-4 text-sm">{order.customer_name || 'Walk-in'}</td>
                    <td className="px-6 py-4 text-sm font-medium tabular-nums">{fmtCurrency(order.total)}</td>
                    <td className="px-6 py-4 text-sm"><span className={`status-badge status-${order.status}`}>{order.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center text-muted-foreground">No recent sales</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
